import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, Typography, Paper, useTheme } from '@mui/material';
import { Block as BlockIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useAuth } from '../../context/AuthContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { JOURNAL_SOURCE_LABEL } from '../../types/finance';
import financeReportsService, {
  FinanceGranularity,
  FinanceSummary,
  FinanceTimeseries,
  CategoryBreakdownItem,
  TopSubmitterItem,
  TopRevenueSourceItem,
  FinanceReportFilters,
} from '../../services/financeReportsService';
import FinanceFilterBar, { DateRange } from '../../components/reports/finance/FinanceFilterBar';
import KpiGrid from '../../components/reports/finance/KpiGrid';
import ChartCard from '../../components/reports/finance/ChartCard';
import TrendChart from '../../components/reports/finance/TrendChart';
import BreakdownDonut from '../../components/reports/finance/BreakdownDonut';
import HorizontalBarList from '../../components/reports/finance/HorizontalBarList';

/**
 * Finance Reports — the elegant, non-duplicating layout.
 *
 * The page tells a four-beat story:
 *
 *   1. KPI strip  — Revenue, Fines Collected, Expenses, Net Cash Flow, Outstanding
 *   2. Trend      — three series (Revenue / Fines / Expenses) over time
 *   3. Composition — Revenue-by-category donut  |  Expenses-by-category donut
 *   4. Rankings   — Top revenue sources         |  Top expense submitters
 *
 * Kill list (things we removed): Expenses-by-Status donut (duplicates the
 * KPI tiles), Expenses-by-Sub-Category card (too niche for a summary page),
 * and the RevenueSection "coming soon" placeholder.
 */

// Build the default "Last 30 days" range in the school's timezone so it
// stays consistent with what FinanceFilterBar produces.
const buildDefaultRange = (timezone: string): DateRange => {
  const nowInTz = toZonedTime(new Date(), timezone);
  const y = nowInTz.getFullYear();
  const m = nowInTz.getMonth();
  const d = nowInTz.getDate();
  const from = fromZonedTime(new Date(y, m, d - 29, 0, 0, 0, 0), timezone);
  const to = fromZonedTime(new Date(y, m, d, 23, 59, 59, 999), timezone);
  return { from, to, label: 'Last 30 days' };
};

const FinanceReportsPage: React.FC = () => {
  const theme = useTheme();
  const { canRead } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const { timezone } = useAuth();

  const [range, setRange] = useState<DateRange>(() => buildDefaultRange(timezone));
  const [granularity, setGranularity] = useState<FinanceGranularity>('day');

  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [timeseries, setTimeseries] = useState<FinanceTimeseries | null>(null);
  const [expenseCategories, setExpenseCategories] = useState<CategoryBreakdownItem[]>([]);
  const [revenueCategories, setRevenueCategories] = useState<CategoryBreakdownItem[]>([]);
  const [topSubmitters, setTopSubmitters] = useState<TopSubmitterItem[]>([]);
  const [topRevenueSources, setTopRevenueSources] = useState<TopRevenueSourceItem[]>([]);
  const [loading, setLoading] = useState(true);

  const filters: FinanceReportFilters = useMemo(
    () => ({
      dateFrom: range.from.toISOString(),
      dateTo: range.to.toISOString(),
      metrics: ['fees', 'revenue', 'fines', 'expenses'],
      granularity,
    }),
    [range.from, range.to, granularity]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Parallel fan-out — every card is fed from one round trip.
      const [s, t, ec, rc, ts, trs] = await Promise.all([
        financeReportsService.getSummary(filters),
        financeReportsService.getTimeseries(filters),
        financeReportsService.getExpensesByCategory({ ...filters, limit: 8 }),
        financeReportsService.getRevenueByCategory({ ...filters, limit: 8 }),
        financeReportsService.getTopSubmitters({ ...filters, limit: 6 }),
        financeReportsService.getTopRevenueSources({ ...filters, limit: 6 }),
      ]);
      setSummary(s);
      setTimeseries(t);
      setExpenseCategories(ec.expenses || []);
      setRevenueCategories(rc.items || []);
      setTopSubmitters(ts.items || []);
      setTopRevenueSources(trs.items || []);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to load finance report', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // ─── Derived chart data (memoised) ────────────────────────────────────────

  const revenueDonutSlices = useMemo(
    () => revenueCategories.map(r => ({
      key: r.categoryId || 'uncat',
      label: r.name,
      amount: r.amount,
      count: r.count,
    })),
    [revenueCategories]
  );

  const expenseDonutSlices = useMemo(
    () => expenseCategories.map(r => ({
      key: r.categoryId || 'uncat',
      label: r.name,
      amount: r.amount,
      count: r.count,
    })),
    [expenseCategories]
  );

  const topRevenueSourceRows = useMemo(
    // A school on the ledger reports by journal source (e.g. FEE_PAYMENT_RECEIVED).
    () => topRevenueSources.map(s => ({
      label: JOURNAL_SOURCE_LABEL[s.name] ?? s.name,
      amount: s.amount,
      count: s.count,
    })),
    [topRevenueSources]
  );

  const topSubmitterRows = useMemo(
    () => topSubmitters.map(u => ({ label: u.name, amount: u.amount, count: u.count })),
    [topSubmitters]
  );

  const trendHasData = !!(
    timeseries?.series.fees?.length
    || timeseries?.series.revenue?.length
    || timeseries?.series.expenses?.length
    || timeseries?.series.fines?.length
  );

  // Permission guard — runs AFTER all hooks are declared, so exiting early
  // here does not skip any hook call.
  if (!canRead('finance_reports')) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', maxWidth: 400, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
          <BlockIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>Access Denied</Typography>
          <Typography variant="body2" color="text.secondary">
            You don't have permission to view Finance Reports.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} color="text.primary">Finance Reports</Typography>
        <Typography variant="body2" color="text.secondary">
          Money in and money out — fee collections, other income, fines and expenses — for any period.
        </Typography>
      </Box>

      {/* Filters */}
      <FinanceFilterBar
        range={range}
        onRangeChange={setRange}
        granularity={granularity}
        onGranularityChange={setGranularity}
      />

      {/* KPI strip */}
      <Box sx={{ mb: 3 }}>
        <KpiGrid summary={summary} loading={loading} />
      </Box>

      {/* Trend — full width, three-series */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <ChartCard
            title="Cash Flow Trend"
            subtitle="Fee collections, other income and fines against expenses"
            loading={loading}
            empty={!loading && !trendHasData}
            height={320}
          >
            <TrendChart
              fees={timeseries?.series.fees || null}
              revenue={timeseries?.series.revenue || null}
              expenses={timeseries?.series.expenses || null}
              fines={timeseries?.series.fines || null}
              granularity={granularity}
              height={320}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Composition — two donuts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Revenue by Category"
            subtitle="Where the money came from"
            loading={loading}
            empty={!loading && revenueDonutSlices.length === 0}
            emptyMessage="No revenue in this window — record entries under Finance → Revenue."
            height={300}
          >
            <BreakdownDonut slices={revenueDonutSlices} totalLabel="Revenue" />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Expenses by Category"
            subtitle="Where the money went"
            loading={loading}
            empty={!loading && expenseDonutSlices.length === 0}
            emptyMessage="No approved expenses in this window."
            height={300}
          >
            <BreakdownDonut slices={expenseDonutSlices} totalLabel="Expenses" />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Rankings — two bar lists */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Top Revenue Sources"
            subtitle="Money-in totalled by source type (revenue, fines, adjustments…)"
            loading={loading}
            empty={!loading && topRevenueSourceRows.length === 0}
            emptyMessage="No money-in events on the ledger in this window."
            height={320}
          >
            <HorizontalBarList rows={topRevenueSourceRows} color={theme.palette.success.main} />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title="Top Expense Submitters"
            subtitle="Employees with the highest approved-expense total"
            loading={loading}
            empty={!loading && topSubmitterRows.length === 0}
            emptyMessage="No approved expenses in this window."
            height={320}
          >
            <HorizontalBarList rows={topSubmitterRows} color={theme.palette.primary.main} />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinanceReportsPage;
